import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconColor, IconName } from '../../icons/types';
import type { UploadProgressItem, UploadProgressStatus } from './types';

type NonUploadingStatus = Exclude<UploadProgressStatus, 'uploading'>;

const STATUS_ICON_NAME: Record<NonUploadingStatus, IconName> = {
  done: 'check-circle-outline',
  error: 'warning-circle',
  pending: 'clock',
};

const STATUS_ICON_COLOR: Record<NonUploadingStatus, IconColor> = {
  done: 'success',
  error: 'red',
  pending: 'grey-02',
};

const STATUS_LABEL_KEY: Record<NonUploadingStatus, string> = {
  done: 'common:Upload status done',
  error: 'common:Upload status error',
  pending: 'common:Upload status pending',
};

function getStatusIconName(status: NonUploadingStatus): IconName {
  return STATUS_ICON_NAME[status];
}

function getStatusIconColor(status: NonUploadingStatus): IconColor {
  return STATUS_ICON_COLOR[status];
}

type UseUploadProgressListParams = Readonly<{
  files: UploadProgressItem[];
  processingLabel?: string;
}>;

type UseUploadProgressListResult = Readonly<{
  announcement: string;
  getStatusIconColor: (status: NonUploadingStatus) => IconColor;
  getStatusIconName: (status: NonUploadingStatus) => IconName;
  getStatusLabel: (status: NonUploadingStatus) => string;
  processingLabelText: string;
}>;

function useUploadProgressList({ files, processingLabel }: UseUploadProgressListParams): UseUploadProgressListResult {
  const { t } = useTranslation(['common']);
  const [announcement, setAnnouncement] = useState<string>('');
  // Seed with the statuses present on first render so files already `done`/`error`
  // at mount are not announced; only real transitions after mount fire the live region.
  const previousStatusesRef = useRef<UploadProgressStatus[]>(files.map((file) => file.status));

  useEffect(() => {
    const previous = previousStatusesRef.current;
    const messages: string[] = [];

    files.forEach((file, index) => {
      const previousStatus = previous[index];
      if (previousStatus === file.status) return;
      if (file.status === 'done') messages.push(t('common:File upload completed', { name: file.name }));
      else if (file.status === 'error') messages.push(t('common:File upload error', { name: file.name }));
    });

    const isAllDone = files.length > 0 && files.every((file) => file.status === 'done');
    const wasAllDone = previous.length === files.length && previous.every((status) => status === 'done');
    if (isAllDone && !wasAllDone) messages.push(t('common:All uploads completed'));

    previousStatusesRef.current = files.map((file) => file.status);

    if (messages.length > 0) setAnnouncement(messages.join('. '));
  }, [files, t]);

  function getStatusLabel(status: NonUploadingStatus): string {
    return t(STATUS_LABEL_KEY[status]);
  }

  const processingLabelText = processingLabel ?? t('common:Processing files');

  return {
    announcement,
    getStatusIconColor,
    getStatusIconName,
    getStatusLabel,
    processingLabelText,
  };
}

export default useUploadProgressList;
